/**
 * Shared kit for the photo-forward launcher templates (Aurelia / Marea / Nord /
 * Terra / Tann). Each template supplies its own chrome but they share:
 *   - the function/visibility model (which functions show),
 *   - the HOME focus nav, which spans the header icons (settings, globe) and
 *     the function dock - so you can navigate up to the icons,
 *   - the themed sub-pages.
 *
 * The function dock lives on HOME only. Sub-pages keep the template's header
 * and show their content full-area; a focusable ◀ Back button sits at the foot
 * (navigate down to it, OK returns home - Esc / hardware Back also work). TV is
 * a launch action (opens the device's TV app), not a sub-page. Screen Share +
 * Information are display/directory pages; Apps + Radio are interactive.
 */
import { useEffect, useMemo, useRef, useState, type RefObject } from "react";
import QRCode from "react-qr-code";
import type { TvPlayerChoice } from "@shared/types/foyer";
import {
  CERTIFIED_LOGO_MAX_HEIGHT,
  CERTIFIED_LOGO_MAX_WIDTH,
} from "@shared/app-certification";
import {
  useScheme,
  stationHue,
  screenTitle,
  screenTitleOverride,
  SECONDARY_SCREENS,
  type ResolvedScheme,
} from "../schemeContext";
import { useNow, formatTime } from "../useNow";
import { useRadioPlayback } from "../useRadioPlayback";
import { useRadioNowPlaying } from "../useRadioNowPlaying";
import { LOCALE_CATALOGUE } from "@shared/i18n";
import { homeBgLayers } from "../bg";
import { launchApp } from "../../../lib/launchApp";
import { openTvForThisScreen, type OpenTvDeps } from "../../../lib/openTv";
import { bridge, type InputSource } from "../../../lib/foyerBridge";
import { castActionLabel, castSteps } from "../../../lib/casting";
import {
  flushEngagement,
  startDwell,
  trackEngagement,
} from "../../../lib/engagement";
import { INFO_READ_SECONDS } from "@shared/types/engagement";
import {
  LAUNCHER_FUNCTIONS,
  LauncherIcon,
  type FunctionDef,
  type FunctionKey,
  type IconName,
  type LauncherScreen,
} from "./icons";
import { useInstagramMedia } from "../useInstagramMedia";
import { SchemeSlot, type SlotStyle } from "../slots/SchemeSlot";
import CustomScreen, { playableItems } from "../custom/CustomScreen";
import OffersScreen from "../offers/OffersScreen";
import VodScreen from "../vod/VodScreen";
import BuiltInPlayer from "../tv/BuiltInPlayer";
import GuideScreen from "../guide/GuideScreen";
import type { InstagramPost } from "@shared/types/foyer";
import { DEFAULT_TV_STREAMING_SLUG } from "@shared/types/foyer";

/**
 * Slot styling for the launcher templates, derived from the shared
 * {@link LauncherTheme}. The promo banner/QR sits on a solid template surface so
 * it stays legible over a full-bleed photo, tinted with the template's accent.
 * Re-exported so each launcher places `<SchemeSlot>` with one consistent style.
 */
export function launcherSlotStyle(T: LauncherTheme): SlotStyle {
  return {
    bg: T.surface,
    ink: T.ink,
    inkDim: T.muted,
    accent: T.accent,
    rule: T.line,
    font: T.sans,
    kickerFont: T.sans,
    radius: 14,
  };
}

export { SchemeSlot };

/** A sub-page the launcher actually renders (TV launches an app instead).
 *  Language + Input open from the header icons (globe / settings). */
type SubScreen =
  | "cast"
  | "apps"
  | "info"
  | "radio"
  | "wifi"
  | "welcome"
  | "relatives"
  | "instagram"
  | "weather"
  | "language"
  | "input"
  | "custom"
  | "offers"
  // Foyer's own player (TV-3a). Both go through `PAGES` like Offers rather than
  // branching before `LauncherPage` like Custom: they WANT the padded sub-page
  // inset, which is why the launcher five needed no per-template edit.
  | "vod"
  | "guide"
  // The built-in player's own shell (DEMO-2). Unlike `vod`/`guide` it escapes
  // the sub-page inset - it carries its OWN left-hand menu, and a menu indented
  // inside another template's padded box reads as a panel rather than an app.
  | "tv";

/** Everything a sub-page needs to paint itself in the template's identity. */
export interface LauncherTheme {
  serif: string;
  sans: string;
  mono?: string;
  /** Solid background sub-pages render on. */
  surface: string;
  card: string;
  chip: string;
  line: string;
  ink: string;
  muted: string;
  accent: string;
  onAccent: string;
  phTone: string;
  phInk: string;
  /** Photo placeholder tone for ImageSlot. */
  photoTone: "light" | "dark";
}

export type Go = (screen: LauncherScreen) => void;

// `homeBgLayers` now lives in ../bg (shared with the older bespoke schemes);
// re-export so existing `import { homeBgLayers } from ".../launcher/kit"` keeps
// working.
export { homeBgLayers };

/** Live clock fields the launcher chrome shows (24h time + long/short date). */
export function useLauncherClock() {
  const now = useNow();
  return {
    time24: formatTime(now),
    day: now.toLocaleDateString("en-US", { weekday: "long" }),
    date: now.toLocaleDateString("en-US", { month: "long", day: "numeric" }),
    dateShort: now.toLocaleDateString("en-US", {
      weekday: "short",
      month: "short",
      day: "numeric",
    }),
  };
}

/** Time-of-day greeting. */
export function greeting(): string {
  const h = new Date().getHours();
  return h < 12 ? "Good morning" : h < 18 ? "Good afternoon" : "Good evening";
}

/**
 * The PMS-sourced guest name for the room this screen is bound to, or
 * `undefined` when we don't know it.
 *
 * PMS check-in data is not wired to the TV yet (see the "Foyer Stay" work), so
 * this returns `undefined` today and every launcher renders an unnamed
 * greeting. Never substitute a placeholder name here - a wrong name in a real
 * room is worse than no name. When PMS lands, this is the single place to read
 * the real `RoomContext.guestName` from.
 */
export function useGuestName(): string | undefined {
  return undefined;
}

/**
 * Build a greeting line that degrades gracefully: `"<lead>, <name>"` when a
 * guest name is known, plain `"<lead>"` when it isn't (so "Welcome back,
 * <blank>" can never render). Blank / whitespace-only names count as unknown.
 */
export function welcomeLine(lead: string, name?: string | null): string {
  const n = name?.trim();
  return n ? `${lead}, ${n}` : lead;
}

/** App tiles are always a light surface so brand logos (Netflix red, Spotify
 *  green, …) read correctly regardless of the template's own palette. */
const APP_TILE_BG = "#f5f4f1";

/**
 * @deprecated The vendor app's package name is no longer decided here. It comes
 * from the global `tvStreamingAppSlug` setting, falling back to
 * `DEFAULT_TV_STREAMING_SLUG` - see `lib/openTv.ts`. Do not launch from it, and
 * do not copy it into a template: five hand-copied copies of this literal are
 * exactly why the CMS's streaming-app setting had no effect on any television.
 *
 * It has **no importers left in this repo**. It survives only because the
 * PLAYER series is being built on several branches at once and one of them may
 * still name it; delete it once that series has landed.
 */
export const TV_APP_PACKAGE = DEFAULT_TV_STREAMING_SLUG;

/**
 * Press "TV": hand the screen to whatever THIS PREMISE'S TV tile points at -
 * the vendor streaming app, or Foyer's own player where the premise has been
 * switched to it (`PremiseConfig.tvPlayer`, TV-6).
 *
 * The decision lives in `lib/openTv.ts` and is made at press time, so it
 * follows the config the television has actually received. On web / CMS preview
 * there is nothing to launch, so this is a no-op and the launcher stays put.
 *
 * `deps.openBuiltin` is how the built-in player gets hooked up once the
 * `vod`/`guide` screens exist; with no callback the tile keeps opening the
 * vendor app, whatever the premise has chosen.
 */
export function launchTv(deps?: OpenTvDeps): void {
  openTvForThisScreen(deps);
}

/**
 * Open the DEVICE'S OWN screen-sharing receiver, and report whether it took the
 * screen.
 *
 * Screen Share is the one launcher function whose UI we should never draw: the
 * TV already ships a receiver (the Philips Cast app, the box's mirroring app)
 * and only that receiver knows the device name, PIN and QR a phone will actually
 * find. So the tile launches it and the launcher stays put behind it.
 *
 * Returns false where there is nothing to open - a Google TV box whose
 * Chromecast built-in is always listening, a Samsung/LG panel entered from its
 * own source list, the CMS preview - and the caller then opens our instruction
 * page instead.
 */
export function openScreenShare(): boolean {
  const info = bridge.getCastingInfo();
  if (!info.supported || !info.launchable) {
    // Say WHY we're about to draw our own page. The shell forwards web warnings
    // to logcat and the fleet console captures them, so this one line is what
    // an engineer with the box in front of them - or a support call two weeks
    // later - reads instead of guessing whether the tile even tried.
    console.warn(
      "[screen-share] no receiver to open; showing instructions instead.",
      JSON.stringify({
        platform: bridge.platform,
        casting: info,
        target: bridge.getScreenShareTarget(),
      })
    );
    return false;
  }
  if (!bridge.launchCastReceiver()) return false;
  // The session is the receiver's from here, so this is the one event we can
  // honestly record: it starts, attributed to the protocol the receiver speaks
  // (we never learn which app on the phone cast to it). Dwell belongs to the
  // native receiver and is deliberately not invented.
  trackEngagement(
    "cast_start",
    info.protocols[0] ?? "unknown",
    castActionLabel(info)
  );
  flushEngagement();
  return true;
}

// TV is the fixed anchor (always present, launches the TV app); Screen Share /
// Apps / Information / Radio can each be toggled off and the four reordered.
const FN_TOGGLE: Record<string, string> = {
  cast: "showCast",
  apps: "showApps",
  info: "showInfo",
  radio: "showRadio",
  wifi: "showWifi",
  welcome: "showWelcome",
  relatives: "showRelatives",
  instagram: "showInstagram",
  weather: "showWeather",
  custom: "showCustom",
  offers: "showOffers",
  vod: "showVod",
  guide: "showGuide",
};

/**
 * The visible launcher functions for a scheme: TV first always, then the
 * operator's `functionOrder`, dropping any toggled off or whose content is
 * empty (no apps → no Apps tile, etc.).
 */
const SECONDARY = new Set<string>(SECONDARY_SCREENS);

/**
 * The enabled SECONDARY functions (Wi-Fi / Welcome / Instagram / Weather), in
 * their canonical order. These are NOT dock tiles - they're listed inside the
 * Information screen. Toggle-driven (the operator opts in via Components, so the
 * toggle has an immediate effect); Weather additionally needs the premise
 * coordinates to render.
 */
export function secondaryFunctions(scheme: ResolvedScheme): FunctionDef[] {
  return LAUNCHER_FUNCTIONS.filter((f) => {
    if (!SECONDARY.has(f.key)) return false;
    const toggle = FN_TOGGLE[f.key];
    if (toggle && !scheme.knobs.toggle(toggle)) return false;
    if (f.key === "weather") {
      return scheme.context.lat != null && scheme.context.lng != null;
    }
    return true;
  });
}

/**
 * The PRIMARY dock functions: TV first always, then the operator's
 * `functionOrder`, dropping any toggled off or with empty content. The
 * secondary screens are excluded here (they live under Information); Information
 * itself stays visible whenever it has sections OR hosts a secondary screen.
 */
export function visibleFunctions(scheme: ResolvedScheme): FunctionDef[] {
  const counts: Partial<Record<FunctionKey, number>> = {
    apps: scheme.content.apps.length,
    info:
      scheme.content.infoSections.length + secondaryFunctions(scheme).length,
    radio: scheme.content.stations.length,
    // An unauthored Custom screen is an empty page, so it earns no dock tile -
    // the same rule Apps and Radio follow.
    custom: playableItems(scheme.content.custom?.items ?? []).length,
    // An Offers tile that opened an empty shop is the failure
    // `docs/room-offers.md` §9 names by name. The feed has already dropped
    // everything that is not live, so its length IS the count; absent (still on
    // the wire, or never asked for) counts as nothing.
    offers: scheme.offers?.offers.length ?? 0,
    // Foyer's own player (TV-3a). Same rule again, and the `undefined` half is
    // the load-bearing one: a dock tile that appears and then disappears when
    // the catalogue answers is worse than one that arrives a beat late.
    vod: (scheme.vod?.rails ?? []).reduce((n, r) => n + r.items.length, 0),
    guide: scheme.guide?.channels.length ?? 0,
  };
  // Visibility is purely toggle + content (NOT order-membership), so a component
  // added to the manifest after a premise saved its order still appears. The
  // order only SORTS; anything missing from it falls to the end (TV stays first).
  const rank = new Map(
    scheme.knobs.order("functionOrder").map((id, i) => [id, i])
  );
  return (
    LAUNCHER_FUNCTIONS.filter((f) => {
      if (SECONDARY.has(f.key)) return false; // secondary screens live under Info
      if (f.key === "tv") return true;
      const toggle = FN_TOGGLE[f.key];
      if (toggle && !scheme.knobs.toggle(toggle)) return false;
      const n = counts[f.key];
      if (n !== undefined && n === 0) return false;
      return true;
    })
      .sort((a, b) => {
        if (a.key === "tv") return -1;
        if (b.key === "tv") return 1;
        return (rank.get(a.key) ?? 999) - (rank.get(b.key) ?? 999);
      })
      // The premise named its own screen, so the dock says that name rather than
      // the manifest word. Done HERE, once, because every launcher template
      // renders `f.label` straight onto its tile.
      .map((f) => ({ ...f, label: screenTitle(scheme, f.key, f.label) }))
  );
}

// ── Home focus nav (header icons + function dock) ───────────────────────────

/** The two header utility icons, in nav order. */
export const HEADER_ICONS = ["settings", "globe"] as const;

export interface HomeFocus {
  /** Focused header icon index (into `headerIcons`), or -1. */
  headerIdx: number;
  /** Focused function index, or -1. */
  funcIdx: number;
  /** The promo banner holds the ring (`POS-4`). False whenever it holds no
   *  live offer, in which case it is not in the chain at all. */
  promoFocused: boolean;
  /** The header utility icons actually shown - `settings` (the external-input
   *  picker) is dropped on devices that can't switch inputs (Android STB / web),
   *  leaving just `globe`. Schemes render THIS, not the static HEADER_ICONS. */
  headerIcons: IconName[];
}

interface HomeNavArgs {
  funcs: FunctionDef[];
  screen: LauncherScreen;
  go: Go;
  scopeRef: RefObject<HTMLElement>;
  /** Header icons sit BEFORE the functions in nav order (true) - so up/left
   *  off the first function reaches them - or after (false, e.g. Terra's
   *  bottom-of-sidebar icons). */
  headerFirst?: boolean;
  /**
   * The home promo slot, when it holds a live offer (`POS-4`).
   *
   * It joins the chain at the END and never at the start: an offer the guest
   * did not ask for may interrupt the eye, never the remote, so the ring still
   * begins on the dock. `active: false` (an empty, dangling or expired slot)
   * leaves the chain exactly as it was rather than adding a cell that lands the
   * ring on nothing.
   */
  promo?: { active: boolean; onPress: () => void };
  /**
   * `PremiseConfig.tvPlayer`, from the config this render is USING.
   *
   * Threaded as a prop rather than read from `useScheme()` for the same reason
   * `theme` is: a launcher must render wherever it is put, and a hook that
   * reaches for a provider makes every caller and every test carry one.
   */
  tvPlayer?: TvPlayerChoice;
}

/**
 * One focus ring across the header icons + the function dock on HOME. Up/Left
 * step toward the start, Down/Right toward the end (clamped), so from TV you
 * press up/left to reach the settings/globe icons. OK launches TV / opens a
 * sub-page; the icons are focusable but inert until their screens exist.
 */
export function useHomeNav({
  funcs,
  screen,
  go,
  scopeRef,
  headerFirst = true,
  promo,
  tvPlayer,
}: HomeNavArgs): HomeFocus {
  // The settings gear opens the external-input picker, so it only shows on
  // devices that can switch inputs (real TVs). Everywhere else just the globe.
  const canSwitchInput = bridge.capabilities().switchInput;
  const headerIcons = useMemo<IconName[]>(
    () => (canSwitchInput ? ["settings", "globe"] : ["globe"]),
    [canSwitchInput]
  );
  const headerCount = headerIcons.length;
  const promoOn = !!promo?.active;
  // The promo cell is the last one in the chain, whichever way round the header
  // icons and the dock sit.
  const total = headerCount + funcs.length + (promoOn ? 1 : 0);
  const promoIdx = promoOn ? total - 1 : -1;
  const firstFunc = headerFirst ? headerCount : 0;
  const [idx, setIdx] = useState(firstFunc);
  const enabled = screen === "home";
  const onPromo = promoOn && idx === promoIdx;
  // The chain can SHRINK under the ring: an offer's window closes, or its
  // catalogue row is deleted, while a guest is sitting on the banner. Pull the
  // ring back into range rather than leaving it on a cell that is gone.
  useEffect(() => {
    setIdx((cur) => Math.min(cur, Math.max(0, total - 1)));
  }, [total]);
  // Read through a ref for the same reason `useLatestHandlers` exists above: the
  // keydown listener is bound once and would otherwise hold the first render's
  // callback forever.
  const promoRef = useRef(promo);
  useEffect(() => {
    promoRef.current = promo;
  });

  useEffect(() => {
    if (!enabled) return;
    function onKey(e: KeyboardEvent) {
      const el = scopeRef.current;
      if (
        !el ||
        (!el.contains(document.activeElement) && document.activeElement !== el)
      ) {
        return;
      }
      const k = e.key;
      const prev = k === "ArrowUp" || k === "ArrowLeft";
      const next = k === "ArrowDown" || k === "ArrowRight";
      const enter = k === "Enter" || k === " ";
      if (!prev && !next && !enter) return;
      e.preventDefault();
      if (enter) {
        // Checked first: the promo cell sits past both groups, so the
        // index arithmetic below would read it as a function (or an icon) that
        // does not exist.
        if (promoOn && idx === promoIdx) {
          promoRef.current?.onPress();
          return;
        }
        const isFunc = headerFirst ? idx >= headerCount : idx < funcs.length;
        if (isFunc) {
          const fIdx = headerFirst ? idx - headerCount : idx;
          const fn = funcs[fIdx];
          if (fn) {
            // TV and Screen Share both hand the screen to something the DEVICE
            // owns - the live-TV app, the built-in cast/mirroring receiver - and
            // the launcher stays put behind it. Screen Share only falls back to
            // our own page on a device with no receiver to open.
            // `openBuiltin` is what makes the premise's choice mean anything:
            // with it, a premise set to `tvPlayer: "foyer"` opens our own
            // guide instead of handing the screen to the vendor app. Without
            // it - and every caller that omits it - the vendor app opens
            // whatever the premise chose, which is the fail-safe direction.
            if (fn.key === "tv")
              launchTv({
                openBuiltin: () => go("tv"),
                // See `openTvForThisScreen`: a CMS preview has a real config
                // and no binding, so the cache read cannot see this choice.
                tvPlayer,
              });
            else if (fn.key !== "cast" || !openScreenShare()) go(fn.key);
          }
        } else {
          // Header icon: settings → external input, globe → language. Dispatch
          // by name (the gear may be absent, shifting indices).
          const hIdx = headerFirst ? idx : idx - funcs.length;
          go(headerIcons[hIdx] === "settings" ? "input" : "language");
        }
        return;
      }
      setIdx((cur) => Math.max(0, Math.min(total - 1, cur + (prev ? -1 : 1))));
    }
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [
    enabled,
    idx,
    total,
    headerCount,
    headerFirst,
    funcs,
    go,
    scopeRef,
    headerIcons,
    promoOn,
    promoIdx,
    tvPlayer,
  ]);

  if (!enabled) {
    return { headerIdx: -1, funcIdx: -1, promoFocused: false, headerIcons };
  }
  if (onPromo) {
    return { headerIdx: -1, funcIdx: -1, promoFocused: true, headerIcons };
  }
  const isFunc = headerFirst ? idx >= headerCount : idx < funcs.length;
  if (isFunc) {
    return {
      headerIdx: -1,
      funcIdx: headerFirst ? idx - headerCount : idx,
      promoFocused: false,
      headerIcons,
    };
  }
  return {
    headerIdx: headerFirst ? idx : idx - funcs.length,
    funcIdx: -1,
    promoFocused: false,
    headerIcons,
  };
}

// ── Sub-page focus (a tile grid + a focusable Back button beneath it) ────────

interface SubFocus {
  r: number;
  c: number;
  /** True when focus is on the Back button (the row below the grid). */
  isBack: boolean;
}

/**
 * D-pad focus for a sub-page: a `rows`×`cols` tile grid, plus a Back button
 * occupying the row at index `rows`. Down off the last grid row lands on Back;
 * OK on Back (or Esc / hardware Back) returns home; OK on a tile calls
 * `onSelect`. Pass `rows = 0` for a display page with only the Back button.
 */
function useSubNav({
  rows,
  cols,
  scopeRef,
  onSelect,
  onBack,
}: {
  rows: number;
  cols: number;
  scopeRef: RefObject<HTMLElement>;
  onSelect?: (i: number) => void;
  onBack: () => void;
}): SubFocus {
  const [pos, setPos] = useState({ r: 0, c: 0 });
  useEffect(() => {
    function onKey(e: KeyboardEvent) {
      const el = scopeRef.current;
      if (
        !el ||
        (!el.contains(document.activeElement) && document.activeElement !== el)
      ) {
        return;
      }
      const k = e.key;
      if (k === "Escape" || k === "Backspace") {
        e.preventDefault();
        onBack();
        return;
      }
      if (k === "Enter" || k === " ") {
        e.preventDefault();
        if (pos.r >= rows) onBack();
        else onSelect?.(pos.r * cols + pos.c);
        return;
      }
      if (!["ArrowUp", "ArrowDown", "ArrowLeft", "ArrowRight"].includes(k)) {
        return;
      }
      e.preventDefault();
      setPos((p) => {
        let { r, c } = p;
        if (k === "ArrowUp") r = Math.max(0, r - 1);
        if (k === "ArrowDown") r = Math.min(rows, r + 1); // rows = Back row
        if (k === "ArrowLeft" && r < rows) c = Math.max(0, c - 1);
        if (k === "ArrowRight" && r < rows) c = Math.min(cols - 1, c + 1);
        return { r, c };
      });
    }
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [rows, cols, pos.r, pos.c, onSelect, onBack, scopeRef]);
  return { r: pos.r, c: pos.c, isBack: pos.r >= rows };
}

function PageHead({
  theme,
  eyebrow,
  title,
  sub,
}: {
  theme: LauncherTheme;
  eyebrow: string;
  title: string;
  sub?: string;
}) {
  return (
    <div style={{ marginBottom: 28, flex: "0 0 auto" }}>
      <div
        style={{
          fontSize: 13,
          letterSpacing: "0.28em",
          textTransform: "uppercase",
          color: theme.accent,
          fontWeight: 600,
          marginBottom: 12,
        }}
      >
        {eyebrow}
      </div>
      <div
        style={{
          fontFamily: theme.serif,
          fontSize: 56,
          fontWeight: 600,
          lineHeight: 1,
          letterSpacing: "-0.005em",
        }}
      >
        {title}
      </div>
      {sub && (
        <div style={{ fontSize: 19, color: theme.muted, marginTop: 14 }}>
          {sub}
        </div>
      )}
    </div>
  );
}

function Tile({
  theme,
  children,
  style = {},
  focused = false,
  innerRef,
}: {
  theme: LauncherTheme;
  children?: React.ReactNode;
  style?: React.CSSProperties;
  focused?: boolean;
  innerRef?: React.Ref<HTMLDivElement>;
}) {
  return (
    <div
      ref={innerRef}
      style={{
        background: theme.card,
        border: `1px solid ${theme.line}`,
        borderRadius: 16,
        boxShadow: focused ? `0 0 0 3px ${theme.accent}` : "none",
        transition: "box-shadow 0.12s",
        ...style,
      }}
    >
      {children}
    </div>
  );
}

/** The focusable Back button at the foot of every sub-page. */
function BackButton({
  theme,
  focused,
  innerRef,
}: {
  theme: LauncherTheme;
  focused: boolean;
  innerRef?: React.Ref<HTMLDivElement>;
}) {
  return (
    <div
      ref={innerRef}
      style={{
        marginTop: 28,
        alignSelf: "flex-start",
        display: "inline-flex",
        alignItems: "center",
        gap: 10,
        padding: "12px 24px",
        borderRadius: 12,
        background: focused ? theme.accent : theme.chip,
        color: focused ? theme.onAccent : theme.ink,
        border: `1px solid ${focused ? theme.accent : theme.line}`,
        fontFamily: theme.sans,
        fontSize: 16,
        fontWeight: 600,
        letterSpacing: "0.02em",
      }}
    >
      <span style={{ fontSize: 18, lineHeight: 1 }}>◀</span> Back
    </div>
  );
}

/** Shared focus-scroll: keep the focused element (tile or Back) in view. */
function useFocusScroll(nav: SubFocus) {
  const ref = useRef<HTMLDivElement | null>(null);
  useEffect(() => {
    ref.current?.scrollIntoView({ block: "nearest" });
  }, [nav.r, nav.c, nav.isBack]);
  return ref;
}

function PageCast({
  theme,
  go,
  scopeRef,
}: {
  theme: LauncherTheme;
  go: Go;
  scopeRef: RefObject<HTMLElement>;
}) {
  const { context } = useScheme();
  const labelFont = theme.mono || theme.sans;
  const room = context.roomId ? `Room ${context.roomId}` : "Guest TV";
  const nav = useSubNav({
    rows: 0,
    cols: 1,
    scopeRef,
    onBack: () => go("home"),
  });
  const backRef = useFocusScroll(nav);

  // THIS PAGE IS THE FALLBACK, not the casting UI. Any device with a receiver
  // it can open - a Philips Cast app, an Android TV box's mirroring app - never
  // gets here: the Screen Share tile launches that receiver directly
  // (`openScreenShare`), because only the receiver knows the device name, PIN
  // and QR a phone will actually discover. What lands here is a device where
  // casting works but there is nothing to open (Chromecast built-in listening on
  // the house network, a panel entered from its own source list) or nothing at
  // all (the CMS preview) - so the honest thing to render is instructions.
  const canCast = bridge.capabilities().guestCasting;
  // The normalized cross-platform casting model - what the receiver speaks and
  // how the guest reaches it. Drives the instruction copy below so a Samsung or
  // LG panel says "Smart View" where a MediaSuite says "Cast", with no
  // platform branching in the view.
  const castInfo = bridge.getCastingInfo();

  // A Screen-Share visit *is* the cast session from our side of the glass. We
  // attribute it to the PROTOCOL the receiver speaks rather than to a sender
  // app, because the receiver never tells us which app on the phone cast to it -
  // inventing that attribution would be the one number in this report a property
  // could not trust. Both strings are primitives, so the effect runs once per
  // visit rather than on every re-render of `castInfo`.
  const castProtocol = castInfo.protocols?.[0] ?? "unknown";
  const castLabel = castActionLabel(castInfo);
  useEffect(() => {
    if (!canCast) return;
    trackEngagement("cast_start", castProtocol, castLabel);
    return startDwell("cast_dwell", castProtocol, castLabel);
  }, [canCast, castProtocol, castLabel]);
  // There is NO network to join and NO code to scan on this page, on any device
  // we ship. The hotspot model died with Wi-Fi Direct, MediaSuite never reaches
  // this page (its tile opens the Cast app), and a box with Chromecast built-in
  // is found over the house Wi-Fi the guest is already on. So the page is pure
  // instruction: which button to press on the phone, and which name to pick.
  //
  // Name the device the way the GUEST'S phone will list it when the platform
  // tells us, falling back to our room label only when it doesn't - sending
  // someone hunting for "Room 214" in a list that says "Android TV" is how an
  // instruction page becomes useless.
  const castName = castInfo.deviceName?.trim() || room;
  const steps = castSteps({
    info: canCast ? castInfo : null,
    deviceName: castName,
  });
  return (
    <>
      <PageHead
        theme={theme}
        eyebrow="Screen Share"
        title="Share your screen"
        sub="Mirror your phone, tablet or laptop onto this television."
      />
      <div
        style={{
          display: "grid",
          gridTemplateColumns: "repeat(3, 1fr)",
          gap: 18,
          marginBottom: 18,
        }}
      >
        {steps.map(({ n, title: t, detail: d }) => (
          <Tile key={n} theme={theme} style={{ padding: "28px 26px" }}>
            <div
              style={{
                fontFamily: theme.serif,
                fontSize: 32,
                fontWeight: 600,
                color: theme.accent,
              }}
            >
              {n}
            </div>
            <div
              style={{
                fontFamily: theme.serif,
                fontSize: 26,
                fontWeight: 600,
                marginTop: 6,
              }}
            >
              {t}
            </div>
            <div
              style={{
                fontSize: 15.5,
                color: theme.muted,
                marginTop: 8,
                lineHeight: 1.45,
              }}
            >
              {d}
            </div>
          </Tile>
        ))}
      </div>
      <Tile
        theme={theme}
        style={{
          padding: "20px 28px",
          display: "flex",
          alignItems: "center",
          gap: 26,
        }}
      >
        <div style={{ flex: 1 }}>
          <div
            style={{
              fontFamily: labelFont,
              fontSize: 12,
              letterSpacing: "0.2em",
              textTransform: "uppercase",
              color: theme.muted,
            }}
          >
            {castName === room ? "Device name" : "Pick this television"}
          </div>
          <div
            style={{
              fontFamily: theme.serif,
              fontSize: 28,
              fontWeight: 600,
              marginTop: 4,
            }}
          >
            {castName === room ? `${room} · Guest TV` : castName}
          </div>
        </div>
      </Tile>
      <BackButton theme={theme} focused={nav.isBack} innerRef={backRef} />
    </>
  );
}

function PageApps({
  theme,
  go,
  scopeRef,
}: {
  theme: LauncherTheme;
  go: Go;
  scopeRef: RefObject<HTMLElement>;
}) {
  const apps = useScheme().content.apps;
  const cols = 4;
  const rows = Math.max(1, Math.ceil(apps.length / cols));
  const nav = useSubNav({
    rows,
    cols,
    scopeRef,
    onSelect: (i) => {
      const app = apps[i];
      if (app) launchApp(app);
    },
    onBack: () => go("home"),
  });
  const focusIdx = nav.r * cols + nav.c;
  const ref = useFocusScroll(nav);
  return (
    <>
      <PageHead theme={theme} eyebrow="Entertainment" title="Apps" />
      <div
        style={{
          display: "grid",
          gridTemplateColumns: "repeat(4, 1fr)",
          gap: 18,
        }}
      >
        {apps.map((app, i) => {
          const on = !nav.isBack && i === focusIdx;
          // No-logo fallback: the brand colour reads on a light tile (white if
          // that's the brand's own colour → fall back to text, then near-black).
          const notWhite = (c?: string) =>
            !!c && !["#fff", "#ffffff", "white"].includes(c.toLowerCase());
          const brandInk = notWhite(app.color)
            ? app.color
            : notWhite(app.text)
              ? app.text
              : "#1a1a1a";
          return (
            <Tile
              key={app.id}
              theme={theme}
              focused={on}
              innerRef={on ? ref : undefined}
              style={{
                // 16:9, because that is what the certification asks of the
                // FRAME a certified logo sits in (NFLX-5) - and because app
                // artwork is landscape, so a squatter tile letterboxes every
                // logo, not just the certified one. The ratio drives the
                // height; nothing here may set one, or it wins and the tile
                // silently stops being 16:9.
                aspectRatio: "16 / 9",
                // border-box, or the ratio governs the CONTENT box and the padding is
                // added on top - which renders 1.65:1, not 16:9. Measured, not assumed.
                boxSizing: "border-box",
                padding: 18,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                // Always a light tile so brand logos read - a Netflix logo on a
                // petrol tile never works. A hairline keeps it defined on light
                // themes.
                background: APP_TILE_BG,
                border: "1px solid rgba(0,0,0,0.07)",
              }}
            >
              {app.logoUrl ? (
                <img
                  src={app.logoUrl}
                  alt={app.name}
                  style={{
                    // A certified logo's clear space is set by the brand, not
                    // by this template (NFLX-5).
                    maxWidth: app.certified ? CERTIFIED_LOGO_MAX_WIDTH : "78%",
                    maxHeight: app.certified
                      ? CERTIFIED_LOGO_MAX_HEIGHT
                      : "64%",
                    objectFit: "contain",
                  }}
                />
              ) : (
                <span
                  style={{
                    fontFamily: theme.sans,
                    fontSize: 26,
                    fontWeight: 800,
                    letterSpacing: "0.01em",
                    color: brandInk,
                    whiteSpace: "nowrap",
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    maxWidth: "90%",
                  }}
                >
                  {app.wordmark || app.name}
                </span>
              )}
            </Tile>
          );
        })}
      </div>
      <BackButton
        theme={theme}
        focused={nav.isBack}
        innerRef={nav.isBack ? ref : undefined}
      />
    </>
  );
}

/**
 * Counts an information section as *read* once it has held focus for
 * {@link INFO_READ_SECONDS}.
 *
 * All of a property's information sections live on one screen as tiles, so a
 * plain section view tells an operator that "Information" was opened and
 * nothing about which of their eight pages anyone actually consumed. Focus is
 * the only attention signal a remote control gives us, and a dwell threshold is
 * what separates arrowing *past* a tile from stopping *on* it. Each section is
 * counted at most once per visit to the screen, so holding focus on the
 * restaurant hours does not inflate them by one per second.
 */
function useInfoReadTracking(
  sections: Array<{ id: string; section: string }>,
  focusIdx: number
): void {
  const counted = useRef<Set<string>>(new Set());
  const section = focusIdx >= 0 ? sections[focusIdx] : undefined;
  const id = section?.id;
  const label = section?.section;
  useEffect(() => {
    if (!id || counted.current.has(id)) return;
    const t = setTimeout(() => {
      counted.current.add(id);
      trackEngagement("info_read", id, label);
    }, INFO_READ_SECONDS * 1000);
    return () => clearTimeout(t);
  }, [id, label]);
}

function PageInfo({
  theme,
  go,
  scopeRef,
}: {
  theme: LauncherTheme;
  go: Go;
  scopeRef: RefObject<HTMLElement>;
}) {
  const scheme = useScheme();
  const sections = scheme.content.infoSections;
  // The secondary screens (Wi-Fi / Welcome / Instagram / Weather) live here as
  // navigable link tiles, appended after the info sections in the same grid.
  const secondary = secondaryFunctions(scheme);
  const cols = 3;
  const total = sections.length + secondary.length;
  const rows = Math.max(1, Math.ceil(total / cols));
  const nav = useSubNav({
    rows,
    cols,
    scopeRef,
    onBack: () => go("home"),
    onSelect: (i) => {
      const link = secondary[i - sections.length];
      if (link) go(link.key);
    },
  });
  const focusIdx = nav.r * cols + nav.c;
  const ref = useFocusScroll(nav);
  useInfoReadTracking(sections, nav.isBack ? -1 : focusIdx);
  return (
    <>
      <PageHead theme={theme} eyebrow="Your stay" title="Information" />
      <div
        style={{
          display: "grid",
          gridTemplateColumns: "repeat(3, 1fr)",
          gap: 18,
        }}
      >
        {sections.map((sec, i) => {
          const on = !nav.isBack && i === focusIdx;
          return (
            <Tile
              key={sec.id}
              theme={theme}
              focused={on}
              innerRef={on ? ref : undefined}
              style={{ padding: "26px 30px" }}
            >
              <div
                style={{
                  fontFamily: theme.serif,
                  fontSize: 28,
                  fontWeight: 600,
                  marginBottom: 14,
                }}
              >
                {sec.section}
              </div>
              {sec.items.map((it, j) => (
                <div
                  key={j}
                  style={{
                    fontSize: 16.5,
                    color: theme.muted,
                    marginTop: 6,
                    lineHeight: 1.4,
                  }}
                >
                  <span style={{ color: theme.ink }}>{it.label}</span>
                  {it.value ? ` - ${it.value}` : ""}
                </div>
              ))}
            </Tile>
          );
        })}
        {secondary.map((f, j) => {
          const idx = sections.length + j;
          const on = !nav.isBack && idx === focusIdx;
          return (
            <Tile
              key={f.key}
              theme={theme}
              focused={on}
              innerRef={on ? ref : undefined}
              style={{
                padding: "26px 30px",
                display: "flex",
                alignItems: "center",
                gap: 18,
                cursor: "pointer",
              }}
            >
              <LauncherIcon name={f.icon} size={30} color={theme.accent} />
              <div
                style={{
                  fontFamily: theme.serif,
                  fontSize: 26,
                  fontWeight: 600,
                  flex: 1,
                }}
              >
                {f.label}
              </div>
              <span style={{ color: theme.muted, fontSize: 26 }}>›</span>
            </Tile>
          );
        })}
      </div>
      <BackButton
        theme={theme}
        focused={nav.isBack}
        innerRef={nav.isBack ? ref : undefined}
      />
    </>
  );
}

function PageWifi({
  theme,
  go,
  scopeRef,
}: {
  theme: LauncherTheme;
  go: Go;
  scopeRef: RefObject<HTMLElement>;
}) {
  const wifi = useScheme().content.wifi;
  // Display-only (no focusable tiles) - rows:0 keeps Back the only focus.
  // Back returns to Information (this is a secondary screen listed under it).
  const nav = useSubNav({
    rows: 0,
    cols: 1,
    scopeRef,
    onBack: () => go("info"),
  });
  const label = {
    fontFamily: theme.mono ?? theme.sans,
    fontSize: 13,
    letterSpacing: "0.18em",
    textTransform: "uppercase" as const,
    color: theme.muted,
  };
  const qrPayload = wifi
    ? `WIFI:T:${wifi.password ? "WPA" : "nopass"};S:${wifi.ssid};${
        wifi.password ? `P:${wifi.password};` : ""
      };`
    : "";
  return (
    <>
      <PageHead theme={theme} eyebrow="Connect" title="Wi-Fi" />
      <div style={{ display: "flex", gap: 56, alignItems: "stretch", flex: 1 }}>
        <Tile
          theme={theme}
          focused={false}
          style={{
            padding: "44px 52px",
            flex: 1,
            display: "flex",
            flexDirection: "column",
            justifyContent: "center",
          }}
        >
          <div style={label}>Network</div>
          <div
            style={{
              fontFamily: theme.serif,
              fontSize: 46,
              fontWeight: 600,
              marginTop: 6,
            }}
          >
            {wifi?.ssid}
          </div>
          {wifi?.password ? (
            <>
              <div style={{ ...label, marginTop: 30 }}>Password</div>
              <div
                style={{
                  fontFamily: theme.mono ?? theme.sans,
                  fontSize: 34,
                  letterSpacing: "0.05em",
                  marginTop: 6,
                  color: theme.ink,
                }}
              >
                {wifi.password}
              </div>
            </>
          ) : null}
        </Tile>
        {wifi?.showQr ? (
          <div style={{ display: "flex", alignItems: "center" }}>
            <div style={{ background: "#fff", padding: 22, borderRadius: 18 }}>
              <QRCode
                value={qrPayload}
                size={220}
                bgColor="#fff"
                fgColor="#111"
              />
            </div>
          </div>
        ) : null}
      </div>
      <BackButton theme={theme} focused={nav.isBack} />
    </>
  );
}

function PageWelcome({
  theme,
  go,
  scopeRef,
}: {
  theme: LauncherTheme;
  go: Go;
  scopeRef: RefObject<HTMLElement>;
}) {
  const w = useScheme().content.welcome;
  const nav = useSubNav({
    rows: 0,
    cols: 1,
    scopeRef,
    onBack: () => go("info"),
  });
  return (
    <>
      <PageHead theme={theme} eyebrow="Welcome" title={w?.title || "Welcome"} />
      <Tile
        theme={theme}
        focused={false}
        style={{ padding: "40px 48px", flex: 1 }}
      >
        <div
          style={{
            fontSize: 25,
            lineHeight: 1.65,
            color: theme.ink,
            whiteSpace: "pre-wrap",
            fontFamily: theme.sans,
            maxWidth: 1100,
          }}
        >
          {w?.body}
        </div>
      </Tile>
      <BackButton theme={theme} focused={nav.isBack} />
    </>
  );
}

function PageRelatives({
  theme,
  go,
  scopeRef,
}: {
  theme: LauncherTheme;
  go: Go;
  scopeRef: RefObject<HTMLElement>;
}) {
  const rel = useScheme().content.relatives;
  const sections = rel?.sections ?? [];
  const embedUrl = rel?.embedUrl;
  const cols = 3;
  const rows = embedUrl ? 0 : Math.max(1, Math.ceil(sections.length / cols));
  const nav = useSubNav({ rows, cols, scopeRef, onBack: () => go("home") });
  const focusIdx = nav.r * cols + nav.c;
  const ref = useFocusScroll(nav);
  return (
    <>
      <PageHead theme={theme} eyebrow="Family" title="Relatives" />
      {embedUrl ? (
        <div
          style={{
            flex: 1,
            borderRadius: 18,
            overflow: "hidden",
            border: `1px solid ${theme.line}`,
          }}
        >
          <iframe
            title="Relatives"
            src={embedUrl}
            style={{ width: "100%", height: "100%", border: 0 }}
          />
        </div>
      ) : (
        <div
          style={{
            display: "grid",
            gridTemplateColumns: "repeat(3, 1fr)",
            gap: 18,
          }}
        >
          {sections.map((sec, i) => {
            const on = !nav.isBack && i === focusIdx;
            return (
              <Tile
                key={sec.id}
                theme={theme}
                focused={on}
                innerRef={on ? ref : undefined}
                style={{ padding: "26px 30px" }}
              >
                <div
                  style={{
                    fontFamily: theme.serif,
                    fontSize: 28,
                    fontWeight: 600,
                    marginBottom: 14,
                  }}
                >
                  {sec.section}
                </div>
                {sec.items.map((it, j) => (
                  <div
                    key={j}
                    style={{
                      fontSize: 16.5,
                      color: theme.muted,
                      marginTop: 6,
                      lineHeight: 1.4,
                    }}
                  >
                    <span style={{ color: theme.ink }}>{it.label}</span>
                    {it.value ? ` - ${it.value}` : ""}
                  </div>
                ))}
              </Tile>
            );
          })}
        </div>
      )}
      <BackButton
        theme={theme}
        focused={nav.isBack}
        innerRef={nav.isBack ? ref : undefined}
      />
    </>
  );
}

/** Columns for the feed grid - scale with the post count so it stays balanced. */
function igCols(n: number, max = 4): number {
  if (n <= 1) return 1;
  if (n <= 4) return 2;
  if (n <= 9) return 3;
  return Math.min(4, max);
}

/** Square post grid for the launcher schemes (atelier / mosaic / spectrum). */
function LauncherIgGrid({
  posts,
  theme,
  cols,
}: {
  posts: InstagramPost[];
  theme: LauncherTheme;
  cols: number;
}) {
  return (
    <div
      style={{
        display: "grid",
        gridTemplateColumns: `repeat(${cols}, 1fr)`,
        gap: 16,
        flex: 1,
        alignContent: "start",
        minHeight: 0,
      }}
    >
      {posts.map((p) => (
        <div
          key={p.id}
          style={{
            position: "relative",
            aspectRatio: "1 / 1",
            borderRadius: 16,
            overflow: "hidden",
            background: theme.card,
            border: `1px solid ${theme.line}`,
          }}
        >
          <img
            src={p.thumbnailUrl ?? p.mediaUrl}
            alt=""
            loading="lazy"
            style={{
              width: "100%",
              height: "100%",
              objectFit: "cover",
              display: "block",
            }}
          />
        </div>
      ))}
    </div>
  );
}

function PageInstagram({
  theme,
  go,
  scopeRef,
}: {
  theme: LauncherTheme;
  go: Go;
  scopeRef: RefObject<HTMLElement>;
}) {
  const ig = useScheme().content.instagram;
  const ctx = useScheme().context;
  const nav = useSubNav({
    rows: 0,
    cols: 1,
    scopeRef,
    onBack: () => go("info"),
  });
  const handle = ig?.handle?.replace(/^@/, "") ?? "";
  // Legacy back-compat: configs without `display` are pure "invite" screens.
  const display = ig?.display ?? "invite";
  const wantFeed = display !== "invite";
  const wantQr = display !== "feed";
  const feedCount = ig?.feedCount ?? 6;
  const { posts } = useInstagramMedia(ctx.premiseId, feedCount, wantFeed);
  const hasFeed = wantFeed && posts.length > 0;
  // Graceful fallback: a feed that's requested but unavailable (not connected
  // yet, or upstream hiccup) drops back to the follow-us invite card.
  const mode: "feed" | "invite" | "both" = hasFeed ? display : "invite";
  const showQr = mode === "both" || (mode === "invite" && wantQr);

  const label = {
    fontFamily: theme.mono ?? theme.sans,
    fontSize: 13,
    letterSpacing: "0.18em",
    textTransform: "uppercase" as const,
    color: theme.muted,
  };

  const qrBlock = (
    <div style={{ background: "#fff", padding: 22, borderRadius: 18 }}>
      <QRCode
        value={`https://instagram.com/${handle}`}
        size={220}
        bgColor="#fff"
        fgColor="#111"
      />
    </div>
  );

  return (
    <>
      <PageHead theme={theme} eyebrow="Follow us" title="Instagram" />
      {mode === "feed" ? (
        <div
          style={{ display: "flex", flexDirection: "column", flex: 1, gap: 20 }}
        >
          <div style={{ display: "flex", alignItems: "baseline", gap: 16 }}>
            <div
              style={{ fontFamily: theme.serif, fontSize: 34, fontWeight: 600 }}
            >
              @{handle}
            </div>
            <div style={{ ...label, fontSize: 16 }}>instagram.com/{handle}</div>
          </div>
          <LauncherIgGrid
            posts={posts}
            theme={theme}
            cols={igCols(posts.length)}
          />
        </div>
      ) : mode === "both" ? (
        <div
          style={{ display: "flex", gap: 44, alignItems: "stretch", flex: 1 }}
        >
          <LauncherIgGrid
            posts={posts}
            theme={theme}
            cols={igCols(posts.length, 3)}
          />
          <div
            style={{
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              justifyContent: "center",
              gap: 20,
            }}
          >
            {qrBlock}
            <div style={{ ...label, textAlign: "center" }}>@{handle}</div>
          </div>
        </div>
      ) : (
        <div
          style={{ display: "flex", gap: 56, alignItems: "stretch", flex: 1 }}
        >
          <Tile
            theme={theme}
            focused={false}
            style={{
              padding: "44px 52px",
              flex: 1,
              display: "flex",
              flexDirection: "column",
              justifyContent: "center",
            }}
          >
            {ig?.caption ? (
              <div
                style={{
                  fontFamily: theme.sans,
                  fontSize: 23,
                  lineHeight: 1.5,
                  color: theme.muted,
                  marginBottom: 26,
                  maxWidth: 640,
                }}
              >
                {ig.caption}
              </div>
            ) : null}
            <div style={label}>Profile</div>
            <div
              style={{
                fontFamily: theme.serif,
                fontSize: 46,
                fontWeight: 600,
                marginTop: 6,
              }}
            >
              @{handle}
            </div>
            <div
              style={{
                fontFamily: theme.mono ?? theme.sans,
                fontSize: 20,
                marginTop: 14,
                color: theme.muted,
              }}
            >
              instagram.com/{handle}
            </div>
          </Tile>
          {showQr ? (
            <div style={{ display: "flex", alignItems: "center" }}>
              {qrBlock}
            </div>
          ) : null}
        </div>
      )}
      <BackButton theme={theme} focused={nav.isBack} />
    </>
  );
}

// ── Weather ──────────────────────────────────────────────────────────────
// Live forecast from /api/foyer/weather (met.no), keyed on the premise coords.

interface WeatherWire {
  current: { tempC: number; symbol: string };
  days: { date: string; minC: number; maxC: number; symbol: string }[];
  source: string;
}

/** met.no symbol family → a glyph + short label. */
const WX: Record<string, { glyph: string; label: string }> = {
  clearsky: { glyph: "☀", label: "Clear" },
  fair: { glyph: "🌤", label: "Fair" },
  partlycloudy: { glyph: "⛅", label: "Partly cloudy" },
  cloudy: { glyph: "☁", label: "Cloudy" },
  fog: { glyph: "🌫", label: "Fog" },
  lightrain: { glyph: "🌦", label: "Light rain" },
  rain: { glyph: "🌧", label: "Rain" },
  heavyrain: { glyph: "🌧", label: "Heavy rain" },
  rainshowers: { glyph: "🌦", label: "Showers" },
  lightsnow: { glyph: "🌨", label: "Light snow" },
  snow: { glyph: "❄", label: "Snow" },
  heavysnow: { glyph: "❄", label: "Snow" },
  snowshowers: { glyph: "🌨", label: "Snow showers" },
  sleet: { glyph: "🌨", label: "Sleet" },
  thunder: { glyph: "⛈", label: "Thunder" },
};
function wx(symbol: string): { glyph: string; label: string } {
  // Longest-prefix match so "lightrainshowers_day" → "rainshowers"/"lightrain".
  if (WX[symbol]) return WX[symbol];
  const hit = Object.keys(WX)
    .filter((k) => symbol.includes(k))
    .sort((a, b) => b.length - a.length)[0];
  return hit ? WX[hit] : { glyph: "·", label: symbol || "-" };
}
const toF = (c: number) => Math.round(c * (9 / 5) + 32);

function PageWeather({
  theme,
  go,
  scopeRef,
}: {
  theme: LauncherTheme;
  go: Go;
  scopeRef: RefObject<HTMLElement>;
}) {
  const ctx = useScheme().context;
  const units = useScheme().content.weather?.units ?? "c";
  const [data, setData] = useState<WeatherWire | null>(null);
  const [err, setErr] = useState(false);
  const nav = useSubNav({
    rows: 0,
    cols: 1,
    scopeRef,
    onBack: () => go("info"),
  });

  useEffect(() => {
    if (ctx.lat == null || ctx.lng == null) return;
    let alive = true;
    setErr(false);
    fetch(`/api/foyer/weather?lat=${ctx.lat}&lon=${ctx.lng}`)
      .then((r) => (r.ok ? r.json() : Promise.reject(r.status)))
      .then((j: WeatherWire) => alive && setData(j))
      .catch(() => alive && setErr(true));
    return () => {
      alive = false;
    };
  }, [ctx.lat, ctx.lng]);

  const fmt = (c: number) => (units === "f" ? `${toF(c)}°` : `${c}°`);
  const label = {
    fontFamily: theme.mono ?? theme.sans,
    fontSize: 13,
    letterSpacing: "0.18em",
    textTransform: "uppercase" as const,
    color: theme.muted,
  };

  const now = data ? wx(data.current.symbol) : null;
  return (
    <>
      <PageHead
        theme={theme}
        eyebrow={ctx.city ?? "Forecast"}
        title="Weather"
      />
      {err ? (
        <Tile theme={theme} focused={false} style={{ padding: "40px 48px" }}>
          <div style={{ fontSize: 22, color: theme.muted }}>
            Weather is unavailable right now.
          </div>
        </Tile>
      ) : !data ? (
        <Tile theme={theme} focused={false} style={{ padding: "40px 48px" }}>
          <div style={{ fontSize: 22, color: theme.muted }}>Loading…</div>
        </Tile>
      ) : (
        <div
          style={{ display: "flex", gap: 36, alignItems: "stretch", flex: 1 }}
        >
          <Tile
            theme={theme}
            focused={false}
            style={{
              padding: "40px 52px",
              flex: "0 0 36%",
              display: "flex",
              flexDirection: "column",
              justifyContent: "center",
            }}
          >
            <div style={label}>Now</div>
            <div style={{ display: "flex", alignItems: "center", gap: 18 }}>
              <span style={{ fontSize: 88, lineHeight: 1 }}>{now?.glyph}</span>
              <span
                style={{
                  fontFamily: theme.serif,
                  fontSize: 76,
                  fontWeight: 600,
                }}
              >
                {fmt(data.current.tempC)}
              </span>
            </div>
            <div style={{ fontSize: 22, color: theme.muted, marginTop: 6 }}>
              {now?.label}
            </div>
          </Tile>
          <div
            style={{
              flex: 1,
              display: "grid",
              gridTemplateColumns: `repeat(${Math.min(
                4,
                data.days.length
              )}, 1fr)`,
              gap: 18,
            }}
          >
            {data.days.slice(0, 4).map((d) => {
              const w = wx(d.symbol);
              const day = new Date(`${d.date}T12:00:00Z`).toLocaleDateString(
                ctx.locale || "en",
                { weekday: "short" }
              );
              return (
                <Tile
                  key={d.date}
                  theme={theme}
                  focused={false}
                  style={{
                    padding: "24px 18px",
                    textAlign: "center",
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "center",
                    justifyContent: "center",
                  }}
                >
                  <div style={{ ...label, fontSize: 12 }}>{day}</div>
                  <div style={{ fontSize: 46, margin: "8px 0" }}>{w.glyph}</div>
                  <div style={{ fontSize: 22, fontFamily: theme.serif }}>
                    {fmt(d.maxC)}
                  </div>
                  <div style={{ fontSize: 17, color: theme.muted }}>
                    {fmt(d.minC)}
                  </div>
                </Tile>
              );
            })}
          </div>
        </div>
      )}
      <BackButton theme={theme} focused={nav.isBack} />
    </>
  );
}

function PageRadio({
  theme,
  go,
  scopeRef,
}: {
  theme: LauncherTheme;
  go: Go;
  scopeRef: RefObject<HTMLElement>;
}) {
  const stations = useScheme().content.stations;
  const { playingId, toggle, audioRef } = useRadioPlayback(stations);
  const cols = 2;
  const rows = Math.max(1, Math.ceil(stations.length / cols));
  const nav = useSubNav({
    rows,
    cols,
    scopeRef,
    onSelect: (i) => toggle(stations[i]?.id),
    onBack: () => go("home"),
  });
  const focusIdx = nav.r * cols + nav.c;
  const ref = useFocusScroll(nav);
  const station = stations[focusIdx] ?? stations[0];
  const np = useRadioNowPlaying(stations.map((s) => s.metadataId));
  // "Now playing" ONLY when the focused station is the one actually streaming;
  // otherwise the header previews the focused station.
  const isPlaying = !!station && station.id === playingId;
  const live = station?.metadataId ? np[station.metadataId] : undefined;
  const detail = isPlaying
    ? live?.song || live?.program || station?.nowPlaying || station?.tag || ""
    : station?.tag || station?.nowPlaying || "";
  const labelFont = theme.mono || theme.sans;
  return (
    <>
      <PageHead theme={theme} eyebrow="Listen" title="Radio" />
      <Tile
        theme={theme}
        style={{
          padding: "16px 26px",
          display: "flex",
          alignItems: "center",
          gap: 22,
          marginBottom: 16,
          flex: "0 0 auto",
        }}
      >
        <div
          style={{
            display: "flex",
            alignItems: "flex-end",
            gap: 4,
            height: 28,
          }}
        >
          {[16, 26, 12, 22, 18].map((h, i) => (
            <div
              key={i}
              style={{
                width: 5,
                height: isPlaying ? h : 6,
                borderRadius: 2,
                background: isPlaying ? theme.accent : theme.muted,
                transition: "height 0.2s",
              }}
            />
          ))}
        </div>
        <div>
          <div
            style={{
              fontFamily: labelFont,
              fontSize: 12,
              letterSpacing: "0.22em",
              textTransform: "uppercase",
              color: theme.accent,
            }}
          >
            {isPlaying ? "Now playing" : "Preview"}
          </div>
          <div
            style={{
              fontFamily: theme.serif,
              fontSize: 25,
              fontWeight: 600,
              marginTop: 2,
            }}
          >
            {station ? `${station.name}${detail ? ` - ${detail}` : ""}` : "-"}
          </div>
        </div>
      </Tile>
      <div
        style={{
          display: "grid",
          gridTemplateColumns: "repeat(2, 1fr)",
          gap: 14,
        }}
      >
        {stations.map((s, i) => {
          const on = !nav.isBack && i === focusIdx;
          return (
            <Tile
              key={s.id}
              theme={theme}
              focused={on}
              innerRef={on ? ref : undefined}
              style={{
                padding: "16px 22px",
                display: "flex",
                alignItems: "center",
                gap: 18,
              }}
            >
              <div
                style={{
                  width: 54,
                  height: 54,
                  borderRadius: 10,
                  background: theme.chip,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  flex: "0 0 auto",
                  overflow: "hidden",
                  color: `hsl(${stationHue(s.id)} 45% 45%)`,
                }}
              >
                {s.logoUrl ? (
                  <img
                    src={s.logoUrl}
                    alt=""
                    style={{
                      width: "100%",
                      height: "100%",
                      objectFit: "contain",
                    }}
                  />
                ) : (
                  <span
                    style={{
                      fontFamily: labelFont,
                      fontSize: 15,
                      color: theme.accent,
                      fontWeight: 700,
                    }}
                  >
                    {s.name.slice(0, 2)}
                  </span>
                )}
              </div>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div
                  style={{
                    fontFamily: theme.serif,
                    fontSize: 23,
                    fontWeight: 600,
                    whiteSpace: "nowrap",
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                  }}
                >
                  {s.name}
                </div>
                <div style={{ fontSize: 14, color: theme.muted, marginTop: 2 }}>
                  {s.id === playingId
                    ? "● Live"
                    : s.freq
                      ? `FM ${s.freq}`
                      : s.tag}
                </div>
              </div>
            </Tile>
          );
        })}
      </div>
      <BackButton
        theme={theme}
        focused={nav.isBack}
        innerRef={nav.isBack ? ref : undefined}
      />
      <audio ref={audioRef} preload="none" />
    </>
  );
}

// ── Header-icon pages ────────────────────────────────────────────────────────

function PageLanguage({
  theme,
  go,
  scopeRef,
}: {
  theme: LauncherTheme;
  go: Go;
  scopeRef: RefObject<HTMLElement>;
}) {
  const { context } = useScheme();
  const codes =
    context.supportedLocales && context.supportedLocales.length
      ? context.supportedLocales
      : context.locale
        ? [context.locale]
        : ["en"];
  const langs = codes.map((code) => {
    const cat = LOCALE_CATALOGUE.find((l) => l.code === code);
    return {
      code,
      label: cat?.label ?? code.toUpperCase(),
      native: cat?.native ?? code.toUpperCase(),
    };
  });
  const cols = Math.min(3, Math.max(1, langs.length));
  const rows = Math.max(1, Math.ceil(langs.length / cols));
  const nav = useSubNav({
    rows,
    cols,
    scopeRef,
    onSelect: (i) => {
      const l = langs[i];
      if (l) context.setLocale?.(l.code);
    },
    onBack: () => go("home"),
  });
  const focusIdx = nav.r * cols + nav.c;
  const ref = useFocusScroll(nav);
  return (
    <>
      <PageHead theme={theme} eyebrow="Display language" title="Language" />
      <div
        style={{
          display: "grid",
          gridTemplateColumns: `repeat(${cols}, 1fr)`,
          gap: 16,
        }}
      >
        {langs.map((l, i) => {
          const on = !nav.isBack && i === focusIdx;
          const active = l.code === context.locale;
          return (
            <Tile
              key={l.code}
              theme={theme}
              focused={on}
              innerRef={on ? ref : undefined}
              style={{
                padding: "24px 26px",
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                background: active ? theme.accent : theme.card,
                border: active ? "none" : `1px solid ${theme.line}`,
              }}
            >
              <div>
                <div
                  style={{
                    fontFamily: theme.serif,
                    fontSize: 28,
                    fontWeight: 600,
                    color: active ? theme.onAccent : theme.ink,
                  }}
                >
                  {l.native}
                </div>
                <div
                  style={{
                    fontSize: 14,
                    marginTop: 4,
                    color: active ? theme.onAccent : theme.muted,
                  }}
                >
                  {l.label}
                </div>
              </div>
              {active && (
                <span style={{ color: theme.onAccent, fontSize: 18 }}>●</span>
              )}
            </Tile>
          );
        })}
      </div>
      <BackButton
        theme={theme}
        focused={nav.isBack}
        innerRef={nav.isBack ? ref : undefined}
      />
    </>
  );
}

function PageInput({
  theme,
  go,
  scopeRef,
}: {
  theme: LauncherTheme;
  go: Go;
  scopeRef: RefObject<HTMLElement>;
}) {
  // The real inputs present on this TV (HDMI 1..n, AV, …). Tizen answers
  // asynchronously, so if the first read is empty, re-read shortly after.
  const [inputs, setInputs] = useState<InputSource[]>(() =>
    bridge.listInputs()
  );
  useEffect(() => {
    if (inputs.length) return;
    const t = setTimeout(() => setInputs(bridge.listInputs()), 400);
    return () => clearTimeout(t);
  }, [inputs.length]);

  const cols = 2;
  const rows = Math.max(1, Math.ceil(inputs.length / cols));
  const nav = useSubNav({
    rows,
    cols,
    scopeRef,
    onSelect: (i) => {
      const src = inputs[i];
      if (!src) return;
      if (bridge.setInput(src.id)) {
        // The source is about to take over the screen, so flush rather than
        // buffer - this may be the last thing the web layer gets to do.
        trackEngagement("input_switch", src.id, src.label);
        flushEngagement(true);
      }
      go("home"); // …and leave the picker (the source takes over the screen)
    },
    onBack: () => go("home"),
  });
  const focusIdx = nav.r * cols + nav.c;
  const ref = useFocusScroll(nav);
  return (
    <>
      <PageHead
        theme={theme}
        eyebrow="Settings"
        title="External input"
        sub="Switch to a device connected to this TV."
      />
      {inputs.length === 0 ? (
        <Tile theme={theme} focused={false} style={{ padding: "26px 28px" }}>
          <span style={{ fontSize: 22, color: theme.muted }}>
            No external inputs detected.
          </span>
        </Tile>
      ) : (
        <div
          style={{
            display: "grid",
            gridTemplateColumns: "repeat(2, 1fr)",
            gap: 16,
          }}
        >
          {inputs.map((src, i) => {
            const on = !nav.isBack && i === focusIdx;
            return (
              <Tile
                key={src.id}
                theme={theme}
                focused={on}
                innerRef={on ? ref : undefined}
                style={{
                  padding: "26px 28px",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  opacity: src.connected === false ? 0.55 : 1,
                }}
              >
                <span
                  style={{
                    fontFamily: theme.serif,
                    fontSize: 28,
                    fontWeight: 600,
                    color: theme.ink,
                  }}
                >
                  {src.label}
                </span>
                {src.connected === false && (
                  <span
                    style={{
                      color: theme.muted,
                      fontSize: 13,
                      letterSpacing: "0.16em",
                      textTransform: "uppercase",
                    }}
                  >
                    No signal
                  </span>
                )}
              </Tile>
            );
          })}
        </div>
      )}
      <BackButton
        theme={theme}
        focused={nav.isBack}
        innerRef={nav.isBack ? ref : undefined}
      />
    </>
  );
}

/**
 * Room offers on a launcher template (`POS-3`).
 *
 * Unlike Custom, this one WANTS the sub-page inset: it is a page of the hotel's
 * own chrome - a heading, a rail of cards, a detail behind each - not an
 * operator's photograph pretending to be the whole screen. So it renders through
 * `PAGES` like every other sub-page, which is also why the launcher five needed
 * no per-template edit at all.
 *
 * `pad` is zeroed because `LauncherPage` has already inset the box; the TV-safe
 * margin is the host's here, not the screen's.
 */
function PageOffers({
  theme,
  go,
  scopeRef,
}: {
  theme: LauncherTheme;
  go: Go;
  scopeRef: RefObject<HTMLElement>;
}) {
  const scheme = useScheme();
  return (
    <OffersScreen
      scopeRef={scopeRef}
      onBack={() => go("home")}
      pad={{ x: 0, y: 0 }}
      title={screenTitleOverride(scheme, "offers")}
      style={{
        bg: "transparent",
        card: theme.card,
        ink: theme.ink,
        inkDim: theme.muted,
        rule: theme.line,
        accent: theme.accent,
        onAccent: theme.onAccent,
        titleFont: theme.serif,
        font: theme.sans,
        radius: 16,
        placeholder: theme.phTone,
      }}
    />
  );
}

/**
 * Films & series on a launcher template (`TV-3a`).
 *
 * Follows `PageOffers` exactly - through `PAGES`, inside the host's padded
 * sub-page box, `pad` zeroed because `LauncherPage` has already inset it. The
 * catalogue is a page of the property's own chrome, not a full-bleed photograph
 * pretending to be the whole screen, so it has no reason to escape the inset the
 * way `LauncherCustom` does.
 */
function PageVod({
  theme,
  go,
  scopeRef,
}: {
  theme: LauncherTheme;
  go: Go;
  scopeRef: RefObject<HTMLElement>;
}) {
  const scheme = useScheme();
  return (
    <VodScreen
      scopeRef={scopeRef}
      onBack={() => go("home")}
      pad={{ x: 0, y: 0 }}
      title={screenTitleOverride(scheme, "vod")}
      style={launcherPlayerStyle(theme)}
    />
  );
}

/** The TV guide on a launcher template (`TV-3a`). Same shape as `PageVod`. */
function PageGuide({
  theme,
  go,
  scopeRef,
}: {
  theme: LauncherTheme;
  go: Go;
  scopeRef: RefObject<HTMLElement>;
}) {
  const scheme = useScheme();
  return (
    <GuideScreen
      scopeRef={scopeRef}
      onBack={() => go("home")}
      pad={{ x: 0, y: 0 }}
      title={screenTitleOverride(scheme, "guide")}
      style={launcherPlayerStyle(theme)}
    />
  );
}

/**
 * The tokens both player screens read, from one `LauncherTheme`.
 *
 * One function rather than two identical object literals: `VodScreenStyle` and
 * `GuideScreenStyle` are deliberately the same field set, so the day a template
 * wants a different accent on the guide is the day this grows a parameter -
 * until then, two copies would only be two places to forget.
 */
function launcherPlayerStyle(theme: LauncherTheme) {
  return {
    bg: "transparent",
    card: theme.card,
    ink: theme.ink,
    inkDim: theme.muted,
    rule: theme.line,
    accent: theme.accent,
    onAccent: theme.onAccent,
    titleFont: theme.serif,
    font: theme.sans,
    radius: 16,
    placeholder: theme.phTone,
  };
}

const PAGES: Record<
  SubScreen,
  (p: {
    theme: LauncherTheme;
    go: Go;
    scopeRef: RefObject<HTMLElement>;
  }) => React.ReactElement
> = {
  cast: PageCast,
  apps: PageApps,
  info: PageInfo,
  radio: PageRadio,
  wifi: PageWifi,
  welcome: PageWelcome,
  relatives: PageRelatives,
  instagram: PageInstagram,
  weather: PageWeather,
  language: PageLanguage,
  input: PageInput,
  // Never reached through PAGES: the templates branch to `LauncherCustom`
  // before `LauncherPage`, because this screen must escape the padded sub-page
  // box. Listed so the map stays exhaustive - if a template ever forgets the
  // branch, the resident gets the page inset rather than a blank screen.
  custom: LauncherCustom,
  offers: PageOffers,
  vod: PageVod,
  guide: PageGuide,
  // Present so a template that forgets the `LauncherTv` branch degrades to an
  // inset page rather than a blank one - the same safety net `PAGES.custom`
  // exists for.
  tv: LauncherTv,
};

/**
 * Renders the active sub-page in the template's identity (no function dock -
 * that's home-only). Apps/Radio/Info navigate with the d-pad; a focusable ◀
 * Back button sits at the foot.
 */
/**
 * The premise's own page on a launcher template.
 *
 * Rendered OUTSIDE the padded sub-page box every other screen sits in: the
 * operator's picture is the page, and a 128px header inset would crop the one
 * thing they uploaded. The template still supplies the bar's chrome, so the
 * screen belongs to the launcher it was opened from.
 */
/**
 * Foyer's own player, full-bleed, on a launcher template (`DEMO-2`).
 *
 * Branches BEFORE `LauncherPage` for the same reason `LauncherCustom` does, and
 * a stronger one: this page carries its own left-hand menu, and a menu drawn
 * inside another template's padded sub-page box - under a header, inset 56px -
 * stops reading as an application and starts reading as a widget. The vendor
 * app it replaces owns the whole panel; so does this.
 *
 * The sections are built from what the room actually HAS. A property with a
 * line-up and no packages gets a one-item menu rather than a "Film & Serier"
 * entry opening an empty shelf - the `undefined`-means-nothing contract the
 * tiles already use, applied one level down.
 */
/**
 * The TV screen, in a launcher template's clothes.
 *
 * A thin wrapper now: the player itself is `BuiltInPlayer`, shared with the
 * three bespoke templates so one button cannot mean two things again. The only
 * thing this adds is turning a `LauncherTheme` into the `VodScreenStyle` every
 * template already speaks.
 */
export function LauncherTv({
  theme,
  go,
  scopeRef,
}: {
  theme: LauncherTheme;
  go: Go;
  scopeRef: RefObject<HTMLElement>;
}) {
  return (
    <BuiltInPlayer
      // `bg` is the template's own surface rather than the inset page's
      // `transparent`: this screen REPLACES the launcher's photograph instead
      // of sitting on it, and left transparent each template's top chrome drew
      // straight through the player — the property's name appeared twice, once
      // across the top and once in the menu.
      style={{ ...launcherPlayerStyle(theme), bg: theme.surface }}
      onExit={() => go("home")}
      scopeRef={scopeRef}
    />
  );
}

export function LauncherCustom({
  theme,
  go,
  scopeRef,
}: {
  theme: LauncherTheme;
  go: Go;
  scopeRef: RefObject<HTMLElement>;
}) {
  return (
    <CustomScreen
      scopeRef={scopeRef}
      onBack={() => go("home")}
      style={{
        barBg: "rgba(0,0,0,0.42)",
        ink: theme.ink,
        accent: theme.accent,
        onAccent: theme.onAccent,
        font: theme.sans,
      }}
    />
  );
}

export function LauncherPage({
  screen,
  theme,
  go,
  scopeRef,
}: {
  screen: Exclude<LauncherScreen, "home">;
  theme: LauncherTheme;
  go: Go;
  scopeRef: RefObject<HTMLElement>;
}) {
  const Page = PAGES[screen as SubScreen] ?? PageApps;
  return (
    <div
      className="launcher-scroll"
      style={{
        width: "100%",
        height: "100%",
        boxSizing: "border-box",
        padding: 56,
        fontFamily: theme.sans,
        color: theme.ink,
        display: "flex",
        flexDirection: "column",
        overflowY: "auto",
        overflowX: "hidden",
      }}
    >
      <Page theme={theme} go={go} scopeRef={scopeRef} />
    </div>
  );
}

// Re-export so templates import everything launcher-related from one place.
export { LAUNCHER_FUNCTIONS, type FunctionDef, type LauncherScreen };
